import React, { useCallback, useEffect } from 'react';
import type { StateDependencyItem } from '@atlassian/jira-aais-dependency-lines-overlay/src/common/ui/types.tsx';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { toIssueId, type IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { DEPENDENCIES_FLYOUT_TYPE } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/constants/overlay.tsx';
import type { OnUnlinkCallback } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/types/dependency.tsx';
import { RoadmapDependencyLines } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-timeline-overlay/dependency-lines/index.tsx';
import type {
	DependencyIds,
	DependencyClickCoords,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-timeline-overlay/dependency-lines/types.tsx';
import {
	RoadmapChartTimelineOverlay,
	type ChartTimelineOverlayRendererProps,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-timeline-overlay/main.tsx';
import { useDependencyTooltip } from '../../../controllers/dependency-tooltip/index.tsx';
import { useTimelineFeatures } from '../../../controllers/features-context/index.tsx';
import type { TimelineViewIssue } from '../../../services/get-timeline-view-issues/types.tsx';
import { useRemoveDependency } from '../../../services/remove-dependency/index.tsx';
import { DependencyFlyout } from './dependency-flyout/index.tsx';
import messages from './messages.tsx';
import { transformDependencies, getSelectedDependency } from './utils.tsx';

type Props = {
	issuesMap: Map<string, TimelineViewIssue>;
	dependencies: StateDependencyItem[];
};

export const ChartTimelineOverlay = ({ issuesMap, dependencies }: Props) => {
	const isLinkingEnabled = useIsLinkingEnabled();
	const { hasIssueLinking } = useTimelineFeatures();
	const { showFlag } = useFlagService();
	const { remove, error: removeDependencyError } = useRemoveDependency();
	const [{ tooltipParams }, { onCloseTooltip, setTooltipParams }] = useDependencyTooltip();

	const {
		issueId,
		position: tooltipPosition,
		dependencies: tooltipDependencies = [],
		dependees: tooltipDependees = [],
	} = tooltipParams || {};

	const onUnlink = useCallback<OnUnlinkCallback>(
		({ dependency, dependee }) => {
			const inwardLinkId = (issuesMap.get(dependee)?.issueLinks || []).find(
				(link) => link.inward?.issueId === dependency,
			)?.issueLinkId;
			const outwardLinkId = (issuesMap.get(dependency)?.issueLinks || []).find(
				(link) => link.outward?.issueId === dependee,
			)?.issueLinkId;
			const linkId = inwardLinkId || outwardLinkId;

			if (!linkId) {
				return;
			}

			const fromIds = inwardLinkId
				? tooltipDependencies.filter((id: IssueId) => String(id) !== String(dependency))
				: tooltipDependencies;
			const toIds = outwardLinkId
				? tooltipDependees.filter((id: IssueId) => String(id) !== String(dependee))
				: tooltipDependees;

			setTooltipParams({
				id: issueId,
				position: tooltipPosition,
				type: DEPENDENCIES_FLYOUT_TYPE,
				links: { fromIds, toIds },
			});
			remove({ linkId, inwardIssueId: dependee, outwardIssueId: dependency });
		},
		[
			issueId,
			issuesMap,
			remove,
			setTooltipParams,
			tooltipDependees,
			tooltipDependencies,
			tooltipPosition,
		],
	);

	const renderDependencyFlyout = useCallback(
		({ id, position }: ChartTimelineOverlayRendererProps) => (
			<DependencyFlyout
				id={id}
				position={position}
				issueId={issueId}
				issuesMap={issuesMap}
				dependees={tooltipDependees}
				dependencies={tooltipDependencies}
				canUnlink={hasIssueLinking && isLinkingEnabled}
				onUnlink={onUnlink}
				onCloseTooltip={onCloseTooltip}
			/>
		),
		[
			issueId,
			issuesMap,
			tooltipDependees,
			tooltipDependencies,
			hasIssueLinking,
			isLinkingEnabled,
			onUnlink,
			onCloseTooltip,
		],
	);

	const onDependencyClick = useCallback(
		({ fromId, toId }: DependencyIds, position: DependencyClickCoords) => {
			setTooltipParams({
				id: toIssueId(fromId),
				type: DEPENDENCIES_FLYOUT_TYPE,
				links: {
					fromIds: [],
					toIds: [toIssueId(toId)],
				},
				position,
			});
		},
		[setTooltipParams],
	);

	const renderDependencyLines = useCallback(
		() => (
			<RoadmapDependencyLines
				dependencies={transformDependencies(dependencies)}
				// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
				selectedDependency={getSelectedDependency(issueId, tooltipDependees)}
				onDependencyClick={onDependencyClick}
			/>
		),
		[dependencies, issueId, tooltipDependees, onDependencyClick],
	);

	const id = tooltipParams ? String(issueId) : undefined;

	useEffect(() => {
		if (removeDependencyError) {
			showFlag({
				type: 'error',
				title: messages.removeDependencyErrorTitle,
				description:
					removeDependencyError instanceof ValidationError
						? [
								messages.removeDependencyDynamicErrorMessage,
								{ error: removeDependencyError.message },
							]
						: messages.removeDependencyErrorMessage,
			});
		}
	}, [removeDependencyError, showFlag]);

	return (
		<RoadmapChartTimelineOverlay
			id={id}
			type={DEPENDENCIES_FLYOUT_TYPE}
			position={tooltipPosition}
			isDependenciesVisible={isLinkingEnabled}
			renderDependencyFlyout={renderDependencyFlyout}
			renderDependencyLines={renderDependencyLines}
		/>
	);
};
